import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState, Region, ResponsiveTarget } from '@immosky/types';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { FocusOrigin } from '@angular/cdk/a11y';
import { ResponsiveService } from '@immosky/object-search-layout';
import { removeRegionFilter } from '@immosky/object-search-filter';

type ChangeCallback = (searchTerm: string) => void;
type TouchedCallback = () => void;

@Component({
  selector: 'lib-search-input',
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchInputComponent),
      multi: true,
    },
  ],
})
export class SearchInputComponent
  implements OnInit, AfterViewInit, AfterViewChecked, ControlValueAccessor
{
  private onChangeCallback!: ChangeCallback;
  private onTouchedCallback!: TouchedCallback;

  hasFocus = false;
  moreChipsCount = 0;

  searchControl = new FormControl('');

  @Input()
  placeholderText = 'filters.search.label';

  @Input()
  activeRegions: Region[] = [];

  @Input()
  withChips = true;

  @ViewChild('container')
  container!: ElementRef<HTMLElement>;

  @ViewChild('chipsContainer')
  chipsContainer!: ElementRef<HTMLElement>;

  @ViewChildren('chip')
  chipQueryList!: QueryList<ElementRef<HTMLElement>>;
  chipElements!: ElementRef<HTMLElement>[];

  @ViewChild('moreChipsIndicator')
  moreChipsIndicator!: ElementRef<HTMLElement>;

  @ViewChild('field')
  field!: ElementRef<HTMLElement>;

  @ViewChild('input')
  input!: ElementRef<HTMLInputElement>;

  @Output()
  escape = new EventEmitter();

  @Output()
  enter = new EventEmitter();

  @Output()
  focused = new EventEmitter();

  @Output()
  openMobileFilters = new EventEmitter();

  get hasChips(): boolean {
    return this.activeRegions.length > 0;
  }

  constructor(
    private ngZone: NgZone,
    private cdRef: ChangeDetectorRef,
    private store: Store<AppState>,
    private responsiveService: ResponsiveService
  ) {}

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(150))
      .subscribe((searchTerm) => {
        this.onChangeCallback?.(searchTerm);
      });
  }

  ngAfterViewInit(): void {
    this.chipQueryList.changes.subscribe((chipQueryList) => {
      this.chipElements = chipQueryList.toArray();
    });
    this.chipElements = this.chipQueryList.toArray();

    window.addEventListener('resize', () => {
      this.updateLayout();
    });
  }

  ngAfterViewChecked(): void {
    this.updateLayout();
  }

  private updateLayout() {
    window.requestAnimationFrame(() => {
      this.layoutChips();
      this.cdRef.detectChanges();
    });
  }

  private layoutChips() {
    if (!this.chipsContainer?.nativeElement) {
      return;
    }

    // Reset Styles
    this.chipsContainer.nativeElement.style.flexBasis = '0';
    this.chipsContainer.nativeElement.style.flexGrow = '1';
    this.field.nativeElement.style.flexGrow = '0';

    for (const chip of this.chipElements) {
      chip.nativeElement.style.marginRight = '';
      chip.nativeElement.style.display = '';
    }

    // Layout chips
    const chipMarginRight = this.getChipMarginRight();
    const availableWidth =
      this.chipsContainer.nativeElement.clientWidth - chipMarginRight;

    const visibleChips: ElementRef<HTMLElement>[] = [];
    let usedWidth = 0;

    for (const chip of this.chipElements) {
      const chipWidth = chip.nativeElement.clientWidth;

      if (usedWidth + chipWidth <= availableWidth) {
        usedWidth += chipWidth + chipMarginRight;
        visibleChips.push(chip);
      } else if (
        this.responsiveService.target(
          ResponsiveTarget.XS,
          ResponsiveTarget.MD
        ) ||
        (this.responsiveService.target(
          ResponsiveTarget.LG,
          ResponsiveTarget.XL
        ) &&
          !this.hasFocus)
      ) {
        chip.nativeElement.style.display = 'none';
      }
    }

    this.chipsContainer.nativeElement.style.flexGrow = '0';
    this.chipsContainer.nativeElement.style.flexBasis = `${usedWidth}px`;
    this.field.nativeElement.style.flexGrow = '1';

    this.moreChipsCount = this.activeRegions.length - visibleChips.length;
  }

  private getChipMarginRight(): number {
    const firstItem = this.chipElements[0]?.nativeElement;
    if (firstItem) {
      return parseInt(window.getComputedStyle(firstItem).marginRight);
    }
    return 0;
  }

  onEscape() {
    this.searchControl.setValue('');
    this.escape.emit();
  }

  onEnter() {
    this.enter.emit();
  }

  removeRegionFilter(region: Region) {
    this.store.dispatch(
      removeRegionFilter({
        region,
      })
    );
    this.focus();
    this.cdRef.markForCheck();
  }

  registerOnChange(fn: ChangeCallback): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: TouchedCallback): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // noop
  }

  writeValue(searchTerm: string): void {
    this.searchControl.setValue(searchTerm, {
      emitEvent: false,
    });
  }

  onSubtreeFocusChange(focusOrigin: FocusOrigin) {
    this.hasFocus = !!focusOrigin;
  }

  trackBy(index: number, region: Region) {
    return region.id;
  }

  onChipClick() {
    if (
      this.responsiveService.target(ResponsiveTarget.XS, ResponsiveTarget.SM)
    ) {
      this.openMobileFilters.emit();
    }
  }

  onMoreChipsClick() {
    if (
      this.responsiveService.target(ResponsiveTarget.XS, ResponsiveTarget.SM)
    ) {
      this.openMobileFilters.emit();
    } else {
      this.focus();
    }
  }

  onBackspace() {
    if (this.searchControl.value === '' && this.activeRegions.length > 0) {
      this.store.dispatch(
        removeRegionFilter({
          region: this.activeRegions[this.activeRegions.length - 1],
        })
      );
    }
  }

  onFocus() {
    this.focused.emit();
  }

  focus() {
    this.input?.nativeElement.focus();
  }
}
